import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
    FaCheckCircle,
    FaTicketAlt,
    FaDownload,
    FaBus,
    FaMapMarkerAlt,
    FaRupeeSign,
    FaCalendarAlt,
    FaClock,
    FaUser,
    FaPhone,
    FaEnvelope,
    FaPrint,
    FaShare,
    FaHome,
    FaArrowLeft,
    FaExclamationTriangle
} from 'react-icons/fa';
import { api } from '../../api';

const BookingConfirmation = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();

    const [bookingData, setBookingData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [error, setError] = useState('');

    // Location state से data लें
    const bookingNumber = location.state?.bookingNumber;
    const paymentId = location.state?.paymentId;
    const amount = location.state?.amount;
    const pickupPoint = location.state?.pickupPoint;
    const dropPoint = location.state?.dropPoint;
    const ticketLink = location.state?.ticketLink;

    useEffect(() => {
        // Check if we have required booking data
        if (!bookingNumber) {
            navigate('/');
            return;
        }
        
        // Simulate data loading or fetch actual data
        const loadBookingData = async () => {
            try {
                setLoading(true);
                
                // For now, use location state data
                const bookingFromState = {
                    bookingNumber,
                    paymentId,
                    amount,
                    pickupPoint,
                    dropPoint,
                    ticketLink,
                    passengers: location.state?.passengers || [],
                    busDetails: location.state?.busDetails || {}
                };
                
                setBookingData(bookingFromState);
                
                // Add small delay for better UX
                setTimeout(() => {
                    setLoading(false);
                }, 500);
                
            } catch (err) {
                console.error('Error loading booking data:', err);
                setError('Could not load booking details. Basic information is shown.');
                setLoading(false);
            }
        };

        loadBookingData();
    }, [bookingNumber, navigate]);

    
    const handleDownloadTicket = () => {
        if (!ticketLink) {
            alert('Ticket URL not found!');
            return;
        }
        
        setDownloading(true);
        
        // सीधे नई टैब में PDF खोलें
        const downloadWindow = window.open(ticketLink, '_blank');
        
        if (!downloadWindow) {
            alert('Please allow popups to download ticket.');
            setDownloading(false);
            return;
        }
        
        // डाउनलोड शुरू होने के लिए थोड़ी देर रुकें
        setTimeout(() => {
            setDownloading(false);
            alert('Ticket download started in new tab!');
        }, 1000);
    };

   
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading booking details...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 p-4 print:bg-white">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8 print:hidden">
                    <div className="w-24 h-24 mx-auto bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mb-4 shadow-lg">
                        <FaCheckCircle className="text-white text-5xl" />
                    </div>
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">Booking Confirmed! 🎉</h1>
                    <p className="text-gray-600 text-lg">Your bus ticket has been booked successfully</p>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 print:hidden">
                        <div className="flex items-center gap-3">
                            <FaExclamationTriangle className="text-red-500 flex-shrink-0" />
                            <div>
                                <p className="text-red-700 font-medium">{error}</p>
                                <p className="text-red-600 text-sm mt-1">Basic booking information is shown below</p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Left Column - Booking Details */}
                    <div className="lg:w-2/3">
                        {/* Booking Summary Card */}
                        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8 border border-gray-200 print:shadow-none print:border">
                            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white print:bg-blue-600">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h2 className="text-2xl font-bold">Booking Summary</h2>
                                        <p className="text-blue-100">Booking ID: {bookingNumber}</p>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm text-blue-200">Payment ID</div>
                                        <div className="text-xl font-bold">{paymentId || 'N/A'}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="p-6">
                               
                                {/* Payment Details */}
                                <div className="mb-8">
                                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                                        <FaRupeeSign className="text-green-600" />
                                        Payment Details
                                    </h3>
                                    <div className="bg-gradient-to-r from-green-50 to-teal-50 p-5 rounded-xl border border-green-200">
                                        <div className="flex justify-between items-center mb-3">
                                            <span className="text-gray-700">Amount Paid:</span>
                                            <span className="text-2xl font-bold text-green-600">₹{amount || '0.00'}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-700">Payment Status:</span>
                                            <span className="bg-green-100 text-green-800 font-semibold px-3 py-1 rounded-full text-sm">
                                                ✓ Successful
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Booking Details */}
                                {bookingData?.passengers && bookingData.passengers.length > 0 && (
                                    <div className="mb-8">
                                        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                                            <FaUser className="text-purple-600" />
                                            Passenger Details
                                        </h3>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                                                <thead className="bg-gray-100">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left text-gray-700 font-semibold">Seat</th>
                                                        <th className="px-4 py-3 text-left text-gray-700 font-semibold">Name</th>
                                                        <th className="px-4 py-3 text-left text-gray-700 font-semibold">Age</th>
                                                        <th className="px-4 py-3 text-left text-gray-700 font-semibold">Gender</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {bookingData.passengers.map((passenger, index) => (
                                                        <tr key={index} className="border-t border-gray-200 hover:bg-gray-50">
                                                            <td className="px-4 py-3">{passenger.seat_no}</td>
                                                            <td className="px-4 py-3 font-medium">{passenger.name}</td>
                                                            <td className="px-4 py-3">{passenger.age}</td>
                                                            <td className="px-4 py-3">{passenger.gender}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                           <div className="flex flex-col gap-4 print:hidden">
                            <button
                                onClick={() => navigate('/my-bookings')}
                                className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition hover:shadow-md"
                            >
                                <FaTicketAlt />
                                View All Bookings
                            </button>

                            <button
                                onClick={() => navigate('/')}
                                className="flex items-center justify-center gap-2 border border-blue-300 text-blue-600 hover:bg-blue-50 font-medium py-3 px-6 rounded-lg transition hover:shadow-md"
                            >
                                <FaBus />
                                Book Another Trip
                            </button>
                        </div>
                    </div>

                    {/* Right Column - Ticket Actions */}
                    <div className="lg:w-1/3">
                        <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 sticky top-6 print:hidden">
                            <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">Ticket Actions</h3>

                            <div className="space-y-4">
                              
                                {/* 2. PDF डाउनलोड करें */}
                                <button
                                    onClick={handleDownloadTicket}
                                    disabled={downloading}
                                    className={`w-full flex items-center justify-center gap-3 p-4 rounded-xl border-2 transition-all ${
                                        downloading
                                            ? 'bg-gray-100 border-gray-300 cursor-wait'
                                            : 'border-green-500 hover:bg-green-50 hover:shadow-md'
                                    }`}
                                >
                                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                        {downloading ? (
                                            <div className="w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                                        ) : (
                                            <FaDownload className="text-green-600 text-lg" />
                                        )}
                                    </div>
                                    <div className="text-left">
                                        <div className="font-semibold text-gray-800">
                                            {downloading ? 'Opening...' : 'Download Ticket'}
                                        </div>
                                        <div className="text-xs text-gray-500">Open PDF for download</div>
                                    </div>
                                </button>

                         
                            </div>

                            {/* Ticket URL Info */}
                            <div className="mt-8 pt-6 border-t border-gray-200">
                                <h4 className="font-bold text-gray-800 mb-3">Ticket Information</h4>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Booking ID:</span>
                                        <span className="font-medium">{bookingNumber}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Status:</span>
                                        <span className="text-green-600 font-medium">Confirmed</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Ticket URL:</span>
                                        <a 
                                            href={ticketLink} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:underline truncate max-w-[150px]"
                                            title="Click to open ticket"
                                        >
                                            Open Ticket
                                        </a>
                                    </div>
                                </div>
                                
                                {/* Direct Link Button */}
                                <div className="mt-4">
                                    <a 
                                        href={ticketLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block w-full text-center bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded-lg transition"
                                    >
                                        Direct Ticket Link: Click Here
                                    </a>
                                </div>
                            </div>
                        </div>

                       
                     
                    </div>

                </div>

                {/* Important Notes */}
                <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-5 print:border print:bg-blue-50">
                    <h4 className="font-bold text-blue-800 mb-3">How to Download/Print Ticket</h4>
                    <ul className="text-sm text-gray-700 space-y-2">
                        <li>1. <strong>View Ticket:</strong> Click to open PDF in new tab</li>
                        <li>2. <strong>Download:</strong> After PDF opens, use browser's download button (CTRL+S)</li>
                        <li>3. <strong>Print:</strong> After PDF opens, use browser's print button (CTRL+P)</li>
                        <li>4. <strong>Direct Link:</strong> Use the "Direct Ticket Link" button above</li>
                        <li>• Browser might ask to allow popups - please allow it</li>
                        <li>• Ticket URL: <a href={ticketLink} target="_blank" className="text-blue-600 underline">Visit and Download</a></li>
                    </ul>
                </div>

                {/* Print Section - Only visible when printing */}
                <div className="hidden print:block mt-8">
                    <div className="text-center mb-6">
                        <h1 className="text-3xl font-bold">BOOKING CONFIRMATION</h1>
                        <p className="text-gray-600">Booking ID: {bookingNumber}</p>
                        <p className="text-gray-600">Ticket URL: {ticketLink}</p>
                    </div>
                    
                    <div className="text-center mt-8 text-sm">
                        <p>** For actual ticket, please visit the URL above **</p>
                        <p>** Or use the buttons on the website to view/download/print **</p>
                    </div>
                </div>

                {/* Footer Note */}
                <div className="mt-8 text-center text-gray-500 text-sm print:hidden">
                    <p>Ticket URL: <a href={ticketLink} target="_blank" className="text-blue-600 underline break-all">Visit & Download</a></p>
                    <p className="mt-1">Simply click the link above to open your ticket in a new tab</p>
                </div>
            </div>
        </div>
    );
};

export default BookingConfirmation;